import { Autocomplete, Box, Card, CardContent, IconButton, Stack, TextField } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { RuleConditionsList } from "../rule-sets/rule-conditions-list"
import { CONDITIONAL_TRADE_FIELDS } from "./conditional-trade-fields"
import { ConditionalProblem } from "./conditional-problem"

/**
 * One conditional trade assignment row. Renders the name, the reused condition picker,
 * the target trade selector, and reorder / delete controls. Validation messages render
 * inline so the user can see why save is blocked.
 *
 * Callbacks update the parent's draft array; persistence happens once when the user
 * confirms the dialog.
 *
 * @param {object} props
 * @param {object} props.conditional - { id, name, conditions, combinator, tradeId, sortOrder }
 * @param {Array<[string,string]>} props.tradeOptions - tuples of [tradeId, tradeName]
 * @param {boolean} props.canMoveUp
 * @param {boolean} props.canMoveDown
 * @param {string[]} props.problems - validation messages for this conditional
 * @param {function} props.onChange - (updatedConditional) => void
 * @param {function} props.onMoveUp
 * @param {function} props.onMoveDown
 * @param {function} props.onDelete
 */
export function ConditionalTradeCard({
    conditional,
    tradeOptions,
    canMoveUp,
    canMoveDown,
    problems,
    onChange,
    onMoveUp,
    onMoveDown,
    onDelete,
}) {
    const tradeOption = tradeOptions.find(matchesSelectedTrade) || null
    const hasProblems = problems && problems.length > 0
    return (
        <Card
            variant="outlined"
            data-cy="conditional-trade-card"
            sx={{ borderColor: hasProblems ? "error.main" : "divider" }}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            label="Conditional name"
                            size="small"
                            fullWidth
                            value={conditional.name || ""}
                            onChange={handleNameChange}
                            data-cy="conditional-trade-name"
                        />
                        <Box sx={{ flexShrink: 0 }}>
                            <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp} aria-label="Move up">
                                <Iconify icon="mdi:arrow-up" width={20} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={onMoveDown}
                                disabled={!canMoveDown}
                                aria-label="Move down"
                            >
                                <Iconify icon="mdi:arrow-down" width={20} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={onDelete}
                                aria-label="Delete conditional"
                                sx={{ color: "error.main" }}
                            >
                                <Iconify icon="mdi:delete-outline" width={20} />
                            </IconButton>
                        </Box>
                    </Stack>

                    {/*
                     * Bound conditionFields restricts the reused picker to the non-circular
                     * field set, per the design call in the spike: trade / hourlyRate /
                     * estimatedAnnualCost all resolve through skillToTrade and would loop.
                     */}
                    <Bound conditionFields={CONDITIONAL_TRADE_FIELDS}>
                        <RuleConditionsList
                            conditions={conditional.conditions || []}
                            combinator={conditional.combinator || "and"}
                            onUpdate={handleConditionsChange}
                            onCombinatorChange={handleCombinatorChange}
                        />
                    </Bound>

                    <Autocomplete
                        size="small"
                        options={tradeOptions}
                        getOptionLabel={(opt) => (Array.isArray(opt) ? opt[1] : "")}
                        isOptionEqualToValue={(opt, val) => opt[0] === (Array.isArray(val) ? val[0] : val)}
                        value={tradeOption}
                        onChange={handleTradeChange}
                        renderInput={(params) => (
                            <TextField {...params} label="Target trade" placeholder="Choose a trade..." />
                        )}
                    />

                    <If value={hasProblems}>
                        <Stack spacing={0.5}>
                            <Repeat list={problems || []} item={<ConditionalProblem />} />
                        </Stack>
                    </If>
                </Stack>
            </CardContent>
        </Card>
    )

    function handleNameChange(e) {
        onChange({ ...conditional, name: e.target.value })
    }

    function handleConditionsChange(nextConditions) {
        onChange({ ...conditional, conditions: nextConditions })
    }

    function handleCombinatorChange(nextCombinator) {
        onChange({ ...conditional, combinator: nextCombinator })
    }

    function handleTradeChange(_, selected) {
        onChange({ ...conditional, tradeId: Array.isArray(selected) ? selected[0] : null })
    }

    function matchesSelectedTrade(t) {
        return t[0] === conditional.tradeId
    }
}
